import { useTranslation, Trans } from 'next-i18next'
import { useMemo } from 'react'

import LoaderSkeleton from '~/shared/components/LoaderSkeleton'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import useWeeklyTimeSpentReport from '~/dashboard/hooks/useWeeklyTimeSpentReport'
import { convertMsToDayHourMinute } from '~/user-stats/utility/date.utils'

import { useAppSelector } from '~/redux/hooks'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import { useLearningStatsQuery } from '~/redux/apis/user-stats-api'
import { formatLearningStats } from '~/statistics/utils/format-stat.utils'
import { useGetInCurriculumV2 } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { useGetDashboardStatsQuery } from '~/statistics-v2/api/statistics.api'
import { formatTimeSpent } from '~/statistics-v2/utils/statistics.utils'

type StatCardProps =
  | {
      srcKey: string
      value: string | number | React.ReactNode
      label: string
      height?: number
      width?: number
    }
  | 'loading'

function StatCard({ stat }: { stat: StatCardProps }): React.ReactElement {
  const props = stat
  if (props === 'loading') {
    return (
      <div className="bg-purple-cosmos border border-white-veil rounded-4 h-17 lg:h-38 flex items-center">
        <LoaderSkeleton className="absolute min-h-full min-w-full rounded bg-transparent" randomStart speed={1200} />
      </div>
    )
  }

  const { srcKey, value, label, height = 55, width = 55 } = props
  return (
    <div className="bg-purple-cosmos border border-white-veil rounded-4 h-17 lg:h-38 flex items-center">
      <div className="flex flex-row lg:flex-col items-center justify-center w-full">
        <ResponsiveImage
          alt="Weekly Stats Icon"
          className="mx-2 md:mx-2 md:mb-2"
          height={height}
          imageClassName="w-10 h-10 lg:w-14 lg:h-14"
          srcKey={srcKey}
          width={width}
        />
        <div className="w-26 md:w-22 lg:w-28">
          <span className="text-md leading-5 font-bold">{value}</span> <br />
          <span className="text-xxs lg:text-xs">{label}</span>
        </div>
      </div>
    </div>
  )
}

function WeeklyStats({ stats }: { stats: StatCardProps[] | 'loading' }): React.ReactElement {
  if (stats === 'loading') {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-2 h- gap-2.5 text-white text-xs md:text-xxs lg:text-xs text-center">
        {[0, 1, 2, 3].map((i) => (
          <StatCard key={i} stat="loading" />
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-2 h- gap-2.5 text-white text-xs md:text-xxs lg:text-xs text-center">
      {stats.map((stat) => (
        <StatCard key={stat === 'loading' ? undefined : stat.label} stat={stat} />
      ))}
    </div>
  )
}

function useWeeklyStats(): StatCardProps[] | 'loading' {
  const { t } = useTranslation(['dashboard', 'statistics'])

  const timeSpent = useWeeklyTimeSpentReport()
  const timeSpan = useMemo<{ startDate: Date; endDate: Date }>(() => {
    const date = new Date()
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7))
    date.setHours(0, 0, 0, 0)
    return { startDate: date, endDate: new Date() }
  }, [])
  const authenticated = useAppSelector(isAuthenticated)

  const { data, isLoading: isLearningStatsLoading } = useLearningStatsQuery(
    {
      startDate: timeSpan.startDate.toISOString(),
      endDate: timeSpan.endDate.toISOString(),
    },
    { skip: !authenticated },
  )
  const { activitiesDone } = formatLearningStats(data)

  const timeSpentThisWeek = timeSpent.data?.dailyUseData.reduce((acc, curr) => acc + curr, 0)
  const timeSpentFormat = useMemo(() => {
    return convertMsToDayHourMinute(timeSpentThisWeek * 60000)
  }, [timeSpentThisWeek])

  if (timeSpent.isLoading || isLearningStatsLoading) return 'loading' as const
  const days = timeSpentFormat.days
    ? Trans({
        t,
        i18nKey: 'statistics:timeStamps.days',
        values: {
          days: timeSpentFormat.days,
        },
      })
    : ''
  const hours = timeSpentFormat.hours
    ? Trans({
        t,
        i18nKey: 'statistics:timeStamps.hours',
        values: {
          hours: timeSpentFormat.hours,
        },
      })
    : ''
  const minutes = Trans({
    t,
    i18nKey: 'statistics:timeStamps.minutes',
    values: {
      minutes: timeSpentFormat.minutes,
    },
  })

  return [
    {
      srcKey: 'dashboard/time-spent.png',
      value: `${days} ${hours} ${minutes}`,
      label: t('dashboard:timeSpent'),
    },
    {
      srcKey: 'dashboard/activities_done.png',
      value: activitiesDone,
      label: t('dashboard:activitiesDone'),
    },
    {
      srcKey: 'dashboard/accuracy.png',
      value: activitiesDone ? `${formatLearningStats(data).activityAccuracy}%` : '0%',
      label: t('dashboard:accuracy'),
    },
    {
      srcKey: 'dashboard/video-minutes.png',
      value: data
        ? t('minWatched', { min: Math.ceil(formatLearningStats(data).watchedVideoDuration / 1000 / 60) })
        : t('minWatched', { min: 0 }),
      label: t('dashboard:videoMinutes'),
    },
  ]
}

function useV2Stats(): StatCardProps[] | 'loading' {
  const { t } = useTranslation(['dashboard', 'statisticsV2'])
  const { data, isLoading } = useGetDashboardStatsQuery()

  if (isLoading || !data) return 'loading' as const

  return [
    {
      srcKey: 'dashboard/time-spent.png',
      value: formatTimeSpent(data.timeSpent),
      label: t('dashboard:timeSpent'),
    },
    {
      srcKey: 'dashboard/activities_done.png',
      value: data.activities,
      label: t('dashboard:activitiesDone'),
    },
    {
      srcKey: 'dashboard/accuracy.png',
      value: `${Math.round(data.accuracy)}%`,
      label: t('dashboard:accuracy'),
    },
    {
      srcKey: 'dashboard/video-minutes.png',
      value: formatTimeSpent(data.videoWatchTime),
      label: t('dashboard:videoMinutes'),
    },
  ]
}

function DashboardWeeklyStats() {
  const stats = useWeeklyStats()
  return <WeeklyStats stats={stats} />
}

function DashboardV2Stats() {
  const stats = useV2Stats()
  return <WeeklyStats stats={stats} />
}

function DashboardStats() {
  const isV2 = useGetInCurriculumV2()
  // Need this extra component because rendering the subcomponents implicitly triggers
  // fetching the stats needed for that component
  return isV2 ? <DashboardV2Stats /> : <DashboardWeeklyStats />
}

export default DashboardStats
